import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { CopyReportResponse } from "../../../../../../../api/types/svTypes.ts";
import { useReportsContext } from "../../../contexts/reports/ReportsContext.ts";
import type { CopyCompany, CopyCompanyReport } from "./CopyCompany";
import copyingStateSlice, { actions } from "./CopyingState";
import type { SelectedCompany } from "./SelectedCompany";

export default function useReportCopying(
  companies: SelectedCompany[],
  copyConditionValues: boolean,
  override: boolean,
  copyAccess: ReportAccessType,
  inheritFromParent: boolean,
  groupsCreating: boolean
) {
  const { actions: reportActions } = useReportsContext();

  const [state, dispatch] = useReducer(copyingStateSlice.reducer, copyingStateSlice.getInitialState());

  const copyCompaniesRef = useRef<CopyCompany[]>([]);
  const canceledRef = useRef(false);

  const reportsToCopy = useMemo(() => {
    return state.companies.flatMap((c) =>
      c.reports.filter((r) => r.copying === false && r.copied === false && !r.error)
    );
  }, [state.companies]);

  useEffect(() => {
    dispatch(actions.setCompaniesAction({ companies }));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    copyCompaniesRef.current = state.companies;
  }, [state.companies]);

  const copyReport = useCallback(
    (report: CopyCompanyReport) => {
      dispatch(actions.updateReportAction({ report, changes: { copying: true } }));

      const ct = reportActions.copyReport(
        {
          reportId: report.report.reportId,
          fromCompanyCode: report.report.clientCode,
          toCompanyCode: report.toCompanyCode,
          validateGroup: true,
          copyConditionValues,
          override,
          groupIdSelectedByUser: report.userSelectedGroupId,
          companySharedAccess: copyAccess,
          inheritFromParent,
        },
        (resp: ApiResponse<CopyReportResponse>) => {
          const changes: Partial<CopyCompanyReport> = { copying: false };
          if (!resp.success || resp.error) {
            changes.error = "An error occurred while copying the report";
          } else if (resp.success && resp.data.hasRequiredDataset === false) {
            changes.error = `Dataset ${resp.data.requiredDataSetName} is missing in the target company.`;
          } else {
            changes.copied = true;
            changes.newReportId = resp.data.reportId;
          }
          dispatch(actions.updateReportAction({ report, changes }));
        },
        () => {
          dispatch(
            actions.updateReportAction({
              report,
              changes: { copying: false, copied: false, error: "An error occurred while copying the report" },
            })
          );
        }
      );
      dispatch(actions.updateReportAction({ report, changes: { ct } }));
    },
    [copyConditionValues, override, copyAccess, inheritFromParent, reportActions]
  );

  useEffect(() => {
    if (canceledRef.current || groupsCreating) return;
    const reports = copyCompaniesRef.current.flatMap((c) => c.reports);
    const inProgressCount = reports.filter((r) => r.copying === true).length;
    const toCopy = reports.filter((r) => !r.copying && !r.copied && !r.error);
    if (inProgressCount < 3 && toCopy.length > 0) {
      copyReport(defined(toCopy[0]));
    }
  }, [copyReport, groupsCreating, reportsToCopy]);

  const retryCopy = useCallback((report: CopyCompanyReport) => {
    dispatch(actions.updateReportAction({ report, changes: { error: undefined, copied: false, copying: false } }));
  }, []);

  const cancel = useCallback(() => {
    canceledRef.current = true;
    copyCompaniesRef.current.forEach((r) => {
      r.reports.forEach((r) => {
        if (r.ct) {
          r.ct.cancel();
        }
      });
    });
  }, []);

  return { copying: state.companies, retryCopy, cancel };
}
